<template>
  <v-list>
    <template v-for="item in items" :key="item">
      <!-- Change Access -->
      <v-list-item
        v-if="item === 'Change Access'"
        link
        data-testid="change-access"
        :prepend-icon="mdiKeyChange"
        title="Change Access"
        @click="changeAccess"
      />
      <v-list-item
        v-else-if="item === 'Profile'"
        link
        exact
        :to="{
          name: 'LoggedInUserProfile'
        }"
        :prepend-icon="mdiAccount"
        title="Profile"
      />
      <!-- Advisor Edit -->
      <v-list-item
        v-else-if="item === 'Advisor Edit'"
        id="v-step-2"
        link
        :to="{
          name: 'LoggedInUserSettings'
        }"
        :prepend-icon="mdiCog"
        title="Settings"
      />
      <!-- Schedule Meeting -->
      <v-list-item
        v-else-if="item === 'Schedule Meeting' && schedulingLink.url"
        link
        target="_blank"
        :href="schedulingLink.url"
        :prepend-icon="mdiCalendar"
        :title="schedulingLink.text"
      />
      <!-- Cobrowse -->
      <v-list-item
        v-else-if="item === 'Cobrowse'"
        link
        title="Start Cobrowse"
        data-testid="start-cobrowse"
        :prepend-icon="mdiMonitorShare"
        @click="startCobrowseSession"
      />

      <!-- BackNine Contacts -->
      <v-list-item
        v-else-if="item === 'BackNine Contacts'"
        :to="{ name: 'Contact' }"
        link
        title="BackNine Contacts"
      >
        <template #prepend>
          <v-icon class="opacity-100">
            <img
              :src="backnines"
              class="greyscale"
              style="height: 24px; width: 24px; object-fit: contain"
            />
          </v-icon>
        </template>
      </v-list-item>
      <!-- Light/Dark Mode -->
      <v-list-item
        v-else-if="item === 'LightDark Mode'"
        :prepend-icon="darkModeAction.icon"
        :title="darkModeAction.title"
        @click="toggleDarkMode"
        @click.stop
      />
      <!-- Divider -->
      <v-divider v-else-if="item === 'Divider'" />
      <!-- Logout -->
      <logout-menu-item v-else-if="item === 'Logout'" />
    </template>
  </v-list>
</template>

<script setup>
import ChangeAccessDialog from "@/dialogs/ChangeAccessDialog.vue";
import LogoutMenuItem from "@/components/LogoutMenuItem.vue";
import { updateUser } from "@/api/users.service";
import { storeToRefs } from "pinia";
import { useUserStore } from "@/stores/user";
import { useSnackbarStore } from "@/stores/snackbar";
import { useDialogStore } from "@/stores/dialog";
import backnines from "@/assets/img/backnines.png";
import {
  mdiKeyChange,
  mdiAccount,
  mdiCog,
  mdiCalendar,
  mdiWeatherSunny,
  mdiThemeLightDark,
  mdiWeatherNight,
  mdiMonitorShare
} from "@mdi/js";
import { computed, markRaw } from "vue";
import { parseErrorMessage } from "@/util/helpers";
import { useCobrowse } from "@/stores/cobrowse";
import ConfirmationDialog from "@/dialogs/ConfirmationDialog.vue";

defineProps({
  items: {
    type: Array,
    required: true
  }
});

const user = useUserStore();
const snackbar = useSnackbarStore();
const cobrowse = useCobrowse();
const dialog = useDialogStore();
const { darkMode, id: userId, loginable } = storeToRefs(user);

const marketingManagerName = computed(
  () => loginable.value.marketing_manager?.name
);
const schedulingLinks = computed(
  () => loginable.value.marketing_manager?.scheduling_links || []
);
const schedulingLink = computed(
  () => schedulingLinks.value?.[0] || { url: "", text: "" }
);
const darkModeAction = computed(() => {
  if (darkMode.value === true) {
    return { icon: mdiWeatherSunny, title: "Enable Light Mode", value: false };
  }
  if (darkMode.value === false) {
    return {
      icon: mdiThemeLightDark,
      title: "Enable Auto Light/Dark Mode",
      value: null
    };
  }
  return { icon: mdiWeatherNight, title: "Enable Dark Mode", value: true };
});

function changeAccess() {
  const activeLogin = user.logins.find(l => l.active);
  dialog.showDialog({
    component: markRaw(ChangeAccessDialog),
    reload: true,
    userId: user.id,
    highestGroup: user.highest_group,
    group: user.group,
    logins: user.logins,
    login: {
      id: activeLogin.id,
      name: activeLogin.name
    }
  });
}

async function toggleDarkMode() {
  darkMode.value = darkModeAction.value.value;
  try {
    await updateUser(userId.value, { dark_mode: darkMode.value });
  } catch (e) {
    snackbar.showErrorSnackbar({ message: parseErrorMessage(e) });
  }
}

async function startCobrowseSession() {
  let cobrowseCode = null;

  let dialogHandle;
  const res = await dialog.showDialog({
    component: markRaw(ConfirmationDialog),
    title: "Start Cobrowse Session",
    subtitle: `By selecting "Confirm", you understand that BOSS will be using a third party tool, CobrowseIO, to share your browser window with ${marketingManagerName.value}.`,
    func: async () => {
      try {
        cobrowseCode = await cobrowse.startCobrowse({
          onActive: () => dialogHandle?.close()
        });
      } catch (e) {
        snackbar.showErrorSnackbar({
          message: "Unable to launch cobrowse session"
        });
      }
    }
  });

  if (!res?.confirm || !cobrowseCode) return;

  dialogHandle = dialog.showDialog({
    returnHandle: true,
    component: markRaw(ConfirmationDialog),
    title: `Share this code with ${marketingManagerName.value}`,
    subtitle: `Your code is: ${cobrowseCode}`,
    hideCancel: true,
    persistent: true,
    confirmText: "End",
    func: cobrowse.endCobrowse
  });
}
</script>
